import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import { User, Role } from "app/auth/models";
import { ToastrService } from "ngx-toastr";
import { LoginResponse, OidcSecurityService } from "angular-auth-oidc-client";
import { NGXLogger } from "ngx-logger";
import { jwtDecode } from "jwt-decode";
import { UserResponse } from "app/models/auth/user.model";
import { Router } from "@angular/router";
import { UserService } from "app/services/auth/user.service";
import { Dialog } from "app/common/dialog";
import { BaseAppService } from "app/services/local-services/base-app-service.service";
import { ConfigurationService } from "app/services/configuration/configuration.service";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "environments/environment";
import * as Sentry from "@sentry/angular";

const COOKIE_CURRENT_USER = "CURRENT_USER";
const COOKIE_ACCESS_TOKEN = "ACCESS_TOKEN";
const COOKIE_CURRENT_WORKER = "CURRENT_WORKER";
const COOKIE_TOKEN_EXPIRATION = "TOKEN_EXPIRATION";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  //public
  public currentUser: Observable<User | null>;
  public currentWorker: Observable<UserResponse | null>;

  //private
  private groupId: string | null = null;
  private currentUserSubject: BehaviorSubject<User | null>;
  private currentWorkerSubject: BehaviorSubject<UserResponse | null>;
  public roles: string[];
  public tokenExpiration: number | null;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(
    private _http: HttpClient,
    private _toastrService: ToastrService,
    private baseAppService: BaseAppService,
    private oidcSecurityService: OidcSecurityService,
    private dialog: Dialog,
    private userService: UserService,
    private _router: Router,
    public logger: NGXLogger,
    private configurationService: ConfigurationService,
    public translateService: TranslateService
  ) {
    // TODO: temporary solution if not receiver user on login and localStorage empty
    // END TODO
    const userString = localStorage.getItem(COOKIE_CURRENT_USER);
    const user =
      userString != null && userString !== "null"
        ? JSON.parse(userString)
        : null;
    const roles = localStorage.getItem(COOKIE_ACCESS_TOKEN);
    this.roles =
      roles != null && roles !== "null" ? jwtDecode<any>(roles).role : [];

    const currentWorkerString = localStorage.getItem(COOKIE_CURRENT_WORKER);
    const currentWorker =
      currentWorkerString != null && currentWorkerString !== "null"
        ? JSON.parse(currentWorkerString)
        : null;

    this.tokenExpiration =
      localStorage.getItem(COOKIE_TOKEN_EXPIRATION) &&
      localStorage.getItem(COOKIE_TOKEN_EXPIRATION) !== "null" &&
      !isNaN(Number(localStorage.getItem(COOKIE_TOKEN_EXPIRATION)))
        ? Number(localStorage.getItem(COOKIE_TOKEN_EXPIRATION))
        : 0;

    // Recover user data
    this.currentUserSubject = new BehaviorSubject<User>(user);
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentWorkerSubject = new BehaviorSubject<UserResponse>(
      currentWorker
    );
    this.currentWorker = this.currentWorkerSubject.asObservable();
    //this.roles = currentWorker ? currentWorker.claims : null;
  }

  // getter: currentUserValue
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public get currentWorkerValue(): UserResponse | null {
    return this.currentWorkerSubject.value;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return (
      this.currentUser &&
      this.currentUserSubject &&
      this.roles &&
      this.roles.includes(Role.Admin)
    );
  }

  get isWorker() {
    return (
      (this.currentWorker &&
        this.currentWorkerSubject &&
        this.roles &&
        this.roles.includes(Role.Worker)) ||
      this.isAdmin
    );
  }

  get isMaintenance() {
    return (
      (this.currentWorker &&
        this.currentWorkerSubject &&
        this.roles &&
        this.roles.includes(Role.Maintenance)) ||
      this.isAdmin
    );
  }

  get workerIsTeamLeader() {
    return this.isWorker && this.currentWorkerValue?.teamLeader;
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(COOKIE_ACCESS_TOKEN);
  }

  public async autoLogin() {
    try {
      const clientId = this.configurationService.getClientId();
      const clientSecret = this.configurationService.getClientSecret();
      if (clientId && clientSecret) {
        const discoveryDocument = await this._http
          .get<any>(
            `${environment.serverAuthUrl}/.well-known/openid-configuration`
          )
          .toPromise();

        if (!discoveryDocument.token_endpoint) {
          return;
        }

        const body = new HttpParams()
          .set("client_id", clientId)
          .set("client_secret", clientSecret)
          .set("grant_type", "client_credentials")
          .set("scope", environment.defaultScope);

        const headers = new HttpHeaders().set(
          "Content-Type",
          "application/x-www-form-urlencoded"
        );

        const tokenResponse = await this._http
          .post<any>(discoveryDocument.token_endpoint, body.toString(), {
            headers,
          })
          .toPromise();

        if (tokenResponse.error) {
          return;
        }

        const data = {
          accessToken: tokenResponse.access_token,
          expiresIn: tokenResponse.expires_in,
          tokenType: tokenResponse.token_type,
          refreshToken: tokenResponse.refresh_token,
          scope: tokenResponse.scope,
          tokenExpiration: new Date(
            Date.now() + tokenResponse.expires_in * 1000
          ),
        };

        // Determine token expiration using JWT exp claim when available
        let tokenExp: number;
        try {
          tokenExp = jwtDecode<any>(tokenResponse.access_token)?.exp;
        } catch (_) {
          tokenExp = 0;
        }
        if (!tokenExp) {
          tokenExp = Date.now() / 1000 + tokenResponse.expires_in;
        }

        //We add an empty user since easier than changing all the code which uses the user cookie
        const emptyUser: User = {};

        localStorage.setItem(COOKIE_CURRENT_USER, JSON.stringify(emptyUser));
        localStorage.setItem(COOKIE_ACCESS_TOKEN, data.accessToken);
        this.currentUserSubject.next(emptyUser);
        if (data.accessToken) {
          const decodedToken = jwtDecode<any>(data.accessToken);
          this.groupId = decodedToken.client_group_id;
          Sentry.setTag("client_group_id", this.groupId);
          this.roles = decodedToken.role;
        }
        this.tokenExpiration = tokenExp;

        localStorage.setItem(
          COOKIE_TOKEN_EXPIRATION,
          this.tokenExpiration?.toString() ?? ""
        );
        //Auto login before it expires
        setTimeout(() => {
          this.autoLogin();
        }, tokenResponse.expires_in * 1000 - 5000);
      } else {
        //Try to log again, maybe network failed
        setTimeout(() => {
          this.autoLogin();
        }, 1000);
      }
    } catch {
      localStorage.removeItem(COOKIE_CURRENT_USER);
      localStorage.removeItem(COOKIE_ACCESS_TOKEN);
      this.currentUserSubject.next(null);
      this.roles = [];
      this.tokenExpiration = null;
      localStorage.removeItem(COOKIE_TOKEN_EXPIRATION);
      //Try to log again, maybe network failed
      setTimeout(() => {
        this.autoLogin();
      }, 1000);
    }
  }
  /**
   * User login
   * @returns user
   */
  async login() {
    let isAuthenticated = false;
    await this.checkAuth().then((x) => (isAuthenticated = x));
    if (!isAuthenticated) {
      await this.oidcSecurityService.authorize();
    }
  }

  async checkAuth(): Promise<boolean> {
    let isAuthenticated = false;
    let loginResponse!: LoginResponse;
    await this.oidcSecurityService
      .checkAuth()
      .toPromise()
      .then((x) => (loginResponse = x as any));
    //this.logger.info('//TEST loginResponse: ', loginResponse)
    // Recover session data
    isAuthenticated = loginResponse.isAuthenticated;
    localStorage.setItem(
      COOKIE_CURRENT_USER,
      JSON.stringify(loginResponse.userData)
    );
    localStorage.setItem(COOKIE_ACCESS_TOKEN, loginResponse.accessToken);
    this.currentUserSubject.next(loginResponse.userData);
    this.roles =
      loginResponse.accessToken != null
        ? jwtDecode<any>(loginResponse.accessToken).role
        : null;
    this.tokenExpiration =
      loginResponse.accessToken != null
        ? Number(jwtDecode<any>(loginResponse.accessToken).exp)
        : null;
    localStorage.setItem(
      COOKIE_TOKEN_EXPIRATION,
      this.tokenExpiration?.toString() ?? ""
    );
    return isAuthenticated;
  }

  loginWorker(code): Observable<Boolean> {
    let subject = new Subject<Boolean>();
    this.userService.validateWorker(code).subscribe({
      next: (data) => {
        localStorage.setItem("CURRENT_WORKER", JSON.stringify(data));
        this.currentWorkerSubject.next(data);
        //Set user context sentry
        Sentry.setUser({
          id: data.workerCode,
          email: data.email,
          username: data.name,
        });
        //this.roles = data.claims;
        this._router.navigate(["worker"]);
        subject.next(true);
      },
      error: (err) => {
        if (err == "Unknown Error") {
          this.dialog.showToastrWsError();
        } else {
          this.dialog.showError(
            "ERROR",
            !this.currentUserValue &&
              Boolean(
                !this.configurationService.getClientId() ||
                  !this.configurationService.getClientSecret()
              )
              ? this.getString("GENERAL.CLIENT_NOT_CONFIGURED")
              : this.getString("GENERAL.INCORRECT_USER_CODE")
          );
        }
        subject.next(false);
      },
    });
    return subject.asObservable();
  }

  isAuthOk(): boolean {
    return !!(
      this.tokenExpiration &&
      this.currentUserValue &&
      this.getAccessToken() != null &&
      this.getAccessToken() != "null" &&
      Date.now() / 1000 < this.tokenExpiration
    );
  }

  /**
   * User logout
   *
   */
  workerLogout() {
    localStorage.removeItem("CURRENT_WORKER");
    localStorage.removeItem("DeviceIp");
    this.baseAppService.deleteLastLabelPrint();
    //Delete devices modes
    Object.keys(localStorage)
      .filter((x) => x.startsWith("DeviceMode_"))
      .forEach((x) => localStorage.removeItem(x));
    this.currentWorkerSubject.next(null);
    this.baseAppService.clearAll();
    try {
      this._toastrService.clear();
    } catch (_) {}
  }

  logout() {
    //Recollir configuracio actual
    const configuration = this.configurationService.getConfiguration();

    this.oidcSecurityService.logoff();
    this.currentUserSubject.next(null);
    this.currentWorkerSubject.next(null);
    this.baseAppService.clearAll();
    localStorage.clear();
    sessionStorage.clear();

    //Guardar de nou
    this.configurationService.setConfiguration(configuration);
  }

  public getString(value: string, params?): string {
    let str = "";
    this.translateService.get(value, params).subscribe((res: string) => {
      return (str = res);
    });
    return str;
  }
}
